import { useEffect, useRef, useState } from "react";
import {
    DialogContent,
    FormControl,
    FormLabel,
    RadioGroup,
    FormControlLabel,
    Radio,
    MenuItem,
    Select,
    InputLabel,
    Box,
    Switch,
    Autocomplete,
    TextField,
    Checkbox,
} from "@mui/material";
import { DatePicker } from "../../components/MoleDatePicker";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDateFns } from "@mui/x-date-pickers/AdapterDateFns";
import { createFilterOptions, Util } from "../../util/Util";
import { useDateRange, getPeriodQueryParams, isActiveDateRange } from "../../hooks/useDateRange";
import { useAppSocket } from "../../providers/AppSocketContext";
import { MIN_DATE, MYSQL_LIMITS } from "../../util/constants";
import RefreshButton from "../../components/RefreshButton";
import { renderCatalogItemOption } from "../../components/itemAutocompleteRenderOption";
import {
	catalogItemAutocompleteInteractionProps,
	handleCatalogItemAutocompleteChange,
	isCatalogItemOptionEqualToValue,
} from "../../components/catalogItemAutocomplete";
import { useAuth } from "../../hooks/useAuth";
import { useUI } from "../../providers/MoleUIContext";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faMagnifyingGlass } from "@fortawesome/free-solid-svg-icons";
import { TimelineOutlined } from "@mui/icons-material";
import {
    MoleCancelButton,
    MoleDialog,
    MoleDialogActions,
    MoleDialogHeader,
    MolePrimaryButton,
    useMoleThemeColor,
} from "../../components/MoleDialogShell";
import VirtualizedListbox from "../../components/VirtualizedListbox";
import { parseISODay } from "../../util/businessClock";

interface Props {
    open: boolean;
    onClose: () => void;
    setFilters: (filters: any) => void;
}

const StockTraceDialog = ({ open, onClose, setFilters }: Props) => {
    const [barcodes, setBarcodes] = useState<any>({});
    const [items, setItems] = useState<any>([]);
    const [depots, setDepots] = useState<any[]>([]);
    const [selectedItem, setSelectedItem] = useState<any>(null);
    const [selectedDepot, setSelectedDepot] = useState<any>(null);
    const [isDepotLoading, setIsDepotLoading] = useState<boolean>(false);
    const [isItemLoading, setIsItemLoading] = useState(false);
    const [fileType, setFileType] = useState<"pdf" | "csv">("pdf");
    const [rangeType, setRangeType] = useState<"current" | "upto" | "between">("current");
    const [errorInput, setErrorInput] = useState("");
    const [includeZeroBalance, setIncludeZeroBalance] = useState(true);
    const [includeSuspended, setIncludeSuspended] = useState(true);
    const [useBarcode, setUseBarcode] = useState(false);
    const [barcode, setBarcode] = useState("");

    const { dateRange, setDateRange } = useDateRange();
    const { appSettings } = useAppSocket();
    const themeColor = useMoleThemeColor();
    const { api } = useAuth();
    const { queueReRun, t } = useUI();

    const minDate = appSettings?.SYSTEM_INCEPTION_DATE?.value || MIN_DATE;

    const fetchDepots = async () => {
        setIsDepotLoading(true);

        const res = await api.get("/depot/meterred", { params: { Scope: "movement" } });

        if (res) {
            if (res.status === 200) {
                setDepots(res.data);
            } else if (res.status === 401 || res.status === 422) {
                queueReRun(fetchDepots);
            } else {
                console.error("Failed to fetch data:", res);
            }
        }

        setIsDepotLoading(false);
    };

    const fetchItems = async () => {
        if ((selectedDepot?.id || -1) < 1) return;

        setIsItemLoading(true);

        const res = await api.get(`depots/${selectedDepot.id}/items`, { params: { Scope: "movement" } });

        if (res) {
            if (res.status === 200) {
                setItems(res.data.items);
                setBarcodes(res.data.barcodes);
            } else if (res.status === 401 || res.status === 422) {
                queueReRun(fetchItems);
            } else {
                console.error("Failed to fetch data:", res);
            }
        }

        setIsItemLoading(false);
    };

    const handleTrace = () => {
        if (!isActiveDateRange(dateRange)) return;
        setFilters({
            Depot: selectedDepot.id,
            Item: selectedItem.id,
            ...getPeriodQueryParams(dateRange),
        });
        onClose();
    };

    const lookupBarcode = (code: string = barcode) => {
        const itemID = barcodes[code];

        if (itemID) {
            const itm = items.find((item: any) => item.id === itemID)
            setSelectedItem(itm);
        } else {
            setErrorInput("barcodeInput");
            setTimeout(() => setErrorInput(""), 2000);
        }
    }

    useEffect(() => {
        if (open) {
            fetchDepots();
        }
    }, [open])

    useEffect(() => {
        if (selectedDepot) {
            fetchItems();
        }
    }, [selectedDepot])

    useEffect(() => {
        if (rangeType === "upto") {
            setDateRange((prev: any) => ({
                ...prev,
                from: (parseISODay(minDate) as Date)
            }));
        }
    }, [rangeType])

    return (
        <MoleDialog open={open} onClose={onClose} maxWidth="xs" fullWidth themeColor={themeColor}>
            <MoleDialogHeader
                title={t("common:stockTrace.title")}
                icon={<TimelineOutlined />}
                onClose={onClose}
                themeColor={themeColor}
            />
            <DialogContent sx={{ pt: 1 }}>
                <Box sx={{ mt: 2 }}>
                    <Autocomplete
                        id="depotInput"
                        size="small"
                        options={depots}
                        filterOptions={createFilterOptions((opt) => opt.DepotName || "")}
                        getOptionLabel={(depot) => depot.DepotName || ""}
                        value={depots.find((d) => d.id === selectedDepot?.id) || null}
                        onChange={(_, newValue) => {
                            setSelectedDepot(newValue);
                        }}
                        loading={isDepotLoading}
                        autoHighlight
                        autoComplete
                        autoSelect
                        disabled={isDepotLoading}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    <span>
                                        {t("common:stockTrace.depot")}&nbsp;&nbsp;
                                        <RefreshButton isWorking={isDepotLoading} onClick={fetchDepots} disabled={isDepotLoading} />
                                    </span>
                                }
                                variant="filled"
                                fullWidth
                            />
                        )}
                    />

                    <TextField
                        id="barcodeInput"
                        size="small"
                        label={t("common:ui.labels.barcode")}
                        variant="filled"
                        fullWidth
                        type="text"
                        value={barcode}
                        inputProps={{ maxLength: MYSQL_LIMITS.VARCHAR(50).maxLength }}
                        slotProps={{
                            input: {
                                endAdornment: <FontAwesomeIcon icon={faMagnifyingGlass} onClick={() => lookupBarcode()} />,
                            }
                        }}
                        onFocus={(e) => e.target.select()}
                        onKeyDown={(e) => {
                            if (e.key === "Enter") {
                                e.preventDefault();
                                lookupBarcode(barcode);
                            }
                        }}
                        onChange={(e) => setBarcode(e.target.value)}
                        disabled={isItemLoading || Object.keys(barcodes).length === 0}
                        error={errorInput === "barcodeInput"}
                        helperText={
                            errorInput === "barcodeInput"
                                ? t("common:stockTrace.barcodeNotFound")
                                : ""
                        }
                    />
                    <Autocomplete
                        id="itemInput"
                        size="small"
                        options={items}
                        ListboxComponent={VirtualizedListbox}
                        filterOptions={createFilterOptions((opt) => opt.ItemName || "")}
                        {...catalogItemAutocompleteInteractionProps}
                        getOptionLabel={(item) => item.ItemName || ""}
                        isOptionEqualToValue={isCatalogItemOptionEqualToValue}
                        renderOption={(props, option) => renderCatalogItemOption(props, option, "both")}
                        value={items?.find((i: any) => i.id === selectedItem?.id) || null}
                        onChange={(_, newValue, reason) => {
                            handleCatalogItemAutocompleteChange(reason, newValue, {
                                onSelect: (id) => {
                                    setSelectedItem(items.find((entry: any) => entry.id === id) || null);
                                },
                                onClear: () => setSelectedItem(null),
                            });
                        }}
                        loading={isItemLoading}
                        disabled={isItemLoading || !selectedDepot}
                        renderInput={(params) => (
                            <TextField
                                {...params}
                                label={
                                    <span>
                                        {t("common:stockTrace.item")}&nbsp;&nbsp;
                                        <RefreshButton isWorking={isItemLoading} onClick={fetchItems} disabled={isItemLoading} />
                                    </span>
                                }
                                placeholder={isItemLoading ? t("common:stockTrace.loadingItems") : t("common:stockTrace.selectItem")}
                                required
                                onFocus={(e) => e.target.select()}
                                variant="filled"
                                fullWidth
                            />
                        )}
                        sx={{
                            '& .MuiAutocomplete-listbox': {
                                padding: 0,             // remove extra padding around list
                            },
                            '& .MuiAutocomplete-option': {
                                padding: '1px 1px',     // make each option compact
                                fontSize: '5rem',   // smaller font
                                minHeight: 'auto',      // remove default minHeight
                            },
                            '& .MuiAutocomplete-popupIndicator': {
                                padding: '4px',         // smaller icon padding
                            },
                        }}
                        ListboxProps={{
                            style: {
                                maxHeight: 400,
                            },
                        }}
                    />

                    <Box sx={{ display: "flex", gap: 2, mt: 2 }}>
                        <LocalizationProvider dateAdapter={AdapterDateFns}>
                            <DatePicker
                                label={t("common:stockTrace.selectStartDate")}
                                value={dateRange?.from}
                                minDate={minDate}
                                maxDate={new Date()}
                                onChange={(newDate) => {
                                    if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < minDate || newDate > new Date()) {
                                        setErrorInput("startDateInput");
                                        setTimeout(() => setErrorInput(""), 1500);
                                        return;
                                    }
                                    const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
                                    setDateRange((prev: any) => ({
                                        ...prev,
                                        from: date
                                    }));
                                }}
                                slotProps={{
                                    textField: {
                                        id: "startDateInput",
                                        size: "small",
                                        fullWidth: true,
                                        variant: "filled", // ✅ makes the textfield filled
                                        error: errorInput === "startDateInput", // ✅ built-in error state
                                        helperText:
                                            errorInput === "startDateInput" ? t("common:validation.invalidDate") : "", // ✅ error message
                                    },
                                }}
                            />
                            <DatePicker
                                label={t("common:stockTrace.selectEndDate")}
                                value={dateRange?.to}
                                minDate={minDate}
                                maxDate={new Date()}
                                onChange={(newDate) => {
                                    if (!newDate || Util.compareStrings(newDate.toString(), "Invalid Date") || newDate < minDate || newDate > new Date()) {
                                        setErrorInput("endDateInput");
                                        setTimeout(() => setErrorInput(""), 1500);
                                        return;
                                    }
                                    const date = newDate instanceof Date ? newDate : newDate.toDate(); // ✅ safe narrowing
                                    setDateRange((prev: any) => ({
                                        ...prev,
                                        to: date
                                    }));
                                }}

                                slotProps={{
                                    textField: {
                                        id: "endDateInput",
                                        size: "small",
                                        fullWidth: true,
                                        variant: "filled", // ✅ makes the textfield filled
                                        error: errorInput === "endDateInput", // ✅ built-in error state
                                        helperText:
                                            errorInput === "endDateInput" ? t("common:validation.invalidDate") : "", // ✅ error message
                                    },
                                }}
                            />

                        </LocalizationProvider>
                    </Box>
                </Box>
            </DialogContent>
            <MoleDialogActions>
                <MoleCancelButton onClick={onClose}>{t("common:ui.buttons.cancel")}</MoleCancelButton>
                <MolePrimaryButton
                    onClick={handleTrace}
                    disabled={!selectedDepot || !selectedItem || !isActiveDateRange(dateRange)}
                    startIcon={<FontAwesomeIcon icon={faMagnifyingGlass} />}
                >
                    {t("common:stockTrace.trace")}
                </MolePrimaryButton>
            </MoleDialogActions>
        </MoleDialog>
    );
};

export default StockTraceDialog;
